
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Mic, Square, ExternalLink, X, Move, Zap, GraduationCap, Activity } from 'lucide-react';
import { runOnce, TAB_ID } from '../services/syncService';
import { AudioRecorder } from '../services/audioUtils';
import { transcribeAudio } from '../services/geminiService';
import { SavedTranscription } from '../types';
import { copyToClipboard } from '../services/clipboardUtils';
import { auth } from '../firebaseConfig';
import { streamTextToCloud, isMobileDevice } from '../services/db';

interface DictaphoneMiniProps {
  onSave: (item: SavedTranscription) => void;
  isVisible: boolean;
  onClose: () => void;
  isStandalone?: boolean;
}

const DictaphoneMini: React.FC<DictaphoneMiniProps> = ({ onSave, isVisible, onClose, isStandalone = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [engineMode, setEngineMode] = useState<'ia' | 'phd'>('ia');
  
  const recorderRef = useRef<AudioRecorder | null>(null);
  const liveManagerRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<any>(null);
  const silenceIntervalRef = useRef<any>(null);
  const silenceStartRef = useRef<number | null>(null);
  const silenceDurationRef = useRef(5);
  const autoStopEnabledRef = useRef(false);
  const isStoppingRef = useRef(false);
  const [autoCopy, setAutoCopy] = useState(true);

  useEffect(() => {
    const handleMirroredText = (e: any) => {
      const { text, status, timestamp } = e.detail;
      if (status === 'final') {
        const taskId = `save_mirror_${timestamp || Date.now()}`;
        runOnce(taskId, async () => {
          onSave({ id: Date.now().toString(), text, mode: 'ia', createdAt: Date.now() });
          if (autoCopy) {
            copyToClipboard(text);
            setShowCopyFeedback(true);
            setTimeout(() => setShowCopyFeedback(false), 2500);
          }
        });
      }
    };
    window.addEventListener('mirrored_text_update', handleMirroredText);
    return () => window.removeEventListener('mirrored_text_update', handleMirroredText);
  }, [onSave, autoCopy]);
  const [position, setPosition] = useState({ x: window.innerWidth - 300, y: window.innerHeight - 150 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const syncSettings = () => {
    const savedAutoCopy = localStorage.getItem('dictaphone_autoCopy');
    const savedAutoStop = localStorage.getItem('dictaphone_autoStop');
    const savedSilence = localStorage.getItem('dictaphone_silence');
    const savedMode = localStorage.getItem('dictaphone_engine_mode');
    
    if (savedAutoCopy !== null) setAutoCopy(savedAutoCopy === 'true');
    if (savedAutoStop !== null) autoStopEnabledRef.current = savedAutoStop === 'true';
    if (savedSilence !== null) silenceDurationRef.current = Math.max(1, Math.min(15, parseInt(savedSilence)));
    if (savedMode !== null) setEngineMode(savedMode === 'phd' || savedMode === 'ia' ? savedMode as any : 'ia');
  };

  useEffect(() => {
    syncSettings();
    window.addEventListener('storage', syncSettings);
    window.addEventListener('dictaphone_settings_update', syncSettings);
    return () => { window.removeEventListener('storage', syncSettings); window.removeEventListener('dictaphone_settings_update', syncSettings); };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isStandalone) return;
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => { if (isDragging) setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y }); };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) { document.addEventListener('mousemove', handleMouseMove); document.addEventListener('mouseup', handleMouseUp); }
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
  }, [isDragging]);

  const stopVisualizer = () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; if (silenceIntervalRef.current) clearInterval(silenceIntervalRef.current); silenceIntervalRef.current = null; if (sourceRef.current) { try { sourceRef.current.disconnect(); } catch (e) { console.warn("Error disconnecting source:", e); } sourceRef.current = null; } isStoppingRef.current = false; };

  const stopRecording = async () => {
      setIsRecording(false); clearInterval(timerRef.current); stopVisualizer();

      if (recorderRef.current) {
        setIsProcessing(true);
        try {
          const { blob, mimeType } = await recorderRef.current.stop();
          const rawText = await transcribeAudio(blob, mimeType, engineMode);
          if (rawText) {
             const text = rawText;
             
             // Si estamos en móvil, enviamos a la nube para el "Modo Espejo"
             if (isMobileDevice() && auth.currentUser) {
               streamTextToCloud(auth.currentUser.uid, text, 'final');
             }

             onSave({ id: Date.now().toString(), text, mode: 'ia', createdAt: Date.now() });
             if (autoCopy) { if (isStandalone) window.focus(); await copyToClipboard(text); setShowCopyFeedback(true); setTimeout(() => setShowCopyFeedback(false), 2500); }
          }
        } catch (e) { console.error("Error stopping recording:", e); } finally { setIsProcessing(false); setRecordingTime(0); isStoppingRef.current = false; }
      }
  };

  const startSilenceDetection = (analyser: AnalyserNode, bufferLength: number, dataArray: any) => {
      if (silenceIntervalRef.current) clearInterval(silenceIntervalRef.current);
      silenceIntervalRef.current = setInterval(() => {
          if (isStoppingRef.current || !autoStopEnabledRef.current || !analyser) return;
          analyser.getByteFrequencyData(dataArray);
          let sum = 0; for(let i = 0; i < bufferLength; i++) sum += dataArray[i];
          const average = sum / bufferLength;
          // UMBRAL AJUSTADO A 35 PARA CONSISTENCIA
          if (average < 35) {
              if (silenceStartRef.current === null) silenceStartRef.current = Date.now();
              else if ((Date.now() - silenceStartRef.current) / 1000 >= silenceDurationRef.current) { isStoppingRef.current = true; stopRecording(); silenceStartRef.current = null; }
          } else silenceStartRef.current = null;
      }, 150);
  };

  const startVisualizer = (stream: MediaStream) => {
    if (!canvasRef.current) return;
    if (!audioContextRef.current) audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume();
    if (!analyserRef.current) { analyserRef.current = audioContextRef.current.createAnalyser(); analyserRef.current.fftSize = 256; analyserRef.current.smoothingTimeConstant = 0.8; }
    try { sourceRef.current = audioContextRef.current.createMediaStreamSource(stream); sourceRef.current.connect(analyserRef.current); } catch { return; }
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength) as any;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    silenceStartRef.current = null; isStoppingRef.current = false;
    startSilenceDetection(analyserRef.current, bufferLength, dataArray);
    const draw = () => {
      if (!analyserRef.current) return;
      animationFrameRef.current = requestAnimationFrame(draw);
      analyserRef.current.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
      const bars = 24; const barWidth = (canvasRef.current!.width / bars) * 0.6;
      for (let i = 0; i < bars; i++) {
         const value = dataArray[Math.floor(i * (bufferLength / (bars * 1.5)))];
         const height = Math.max(4, (value / 255) * (canvasRef.current!.height * 0.8));
         ctx.fillStyle = engineMode === 'phd' ? '#3b82f6' : '#10b981';
         ctx.beginPath();
         if (ctx.roundRect) ctx.roundRect(i * (canvasRef.current!.width / bars) + (canvasRef.current!.width/bars - barWidth)/2, (canvasRef.current!.height - height) / 2, barWidth, height, 2);
         else ctx.rect(i * (canvasRef.current!.width / bars) + (canvasRef.current!.width/bars - barWidth)/2, (canvasRef.current!.height - height) / 2, barWidth, height);
         ctx.fill();
      }
    };
    draw();
  };

  const handlePopOut = () => {
    const width = 320; const height = 450; const left = (window.screen.width - width) / 2; const top = (window.screen.height - height) / 2;
    window.open(`${window.location.origin}${window.location.pathname}?widget=recorder`, `MODULAR_MINI_${Date.now()}`, `width=${width},height=${height},top=${top},left=${left},resizable=yes,alwaysOnTop=yes`);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className={isStandalone ? "fixed inset-0 flex flex-col items-center justify-center bg-gray-900 z-50 text-white" : "fixed z-50"} style={!isStandalone ? { left: position.x, top: position.y } : undefined}>
       {showCopyFeedback && <div className={`bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg z-50 ${isStandalone ? 'fixed top-10 left-1/2 -translate-x-1/2' : 'absolute -top-12 left-1/2 -translate-x-1/2'}`}>Copiado</div>}
       {!isStandalone && (
         <div className="absolute -top-8 right-0 flex items-center space-x-2 bg-gray-800/80 p-1 rounded-full shadow-lg border border-gray-700">
             <div onMouseDown={handleMouseDown} className="w-6 h-6 bg-gray-700 text-gray-300 hover:text-white rounded-full flex items-center justify-center cursor-move"><Move size={12} /></div>
             <button onClick={handlePopOut} className="w-6 h-6 bg-gray-700 text-gray-300 hover:text-white rounded-full flex items-center justify-center"><ExternalLink size={12} /></button>
             <button onClick={onClose} className="w-6 h-6 bg-gray-700 text-gray-300 hover:text-red-400 rounded-full flex items-center justify-center"><X size={12} /></button>
         </div>
       )}
       <div className={`relative flex items-center justify-between bg-gray-900/90 border border-gray-700/50 shadow-2xl transition-all ${isRecording ? 'pl-4 pr-1.5 py-1.5 rounded-full' : 'p-1.5 rounded-full'} ${isStandalone ? 'w-full max-w-sm rounded-3xl p-6 flex-col justify-center' : ''}`}>
          <div className={`relative overflow-hidden transition-all flex items-center ${isRecording ? 'opacity-100' : 'opacity-0 w-0'} ${isStandalone ? 'w-full h-32 opacity-100' : ''}`} style={{ width: isStandalone ? '100%' : (isRecording ? 200 : 0) }}>
             {isRecording && <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex flex-col justify-center"><span className="text-red-400 text-[10px] font-bold animate-pulse flex items-center"><div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></div>REC</span><span className="text-white font-mono font-bold text-sm mt-0.5">{Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}</span></div>}
             <canvas ref={canvasRef} width={isStandalone ? 300 : 160} height={isStandalone ? 100 : 40} className={`ml-auto ${isStandalone ? 'w-full' : ''}`} />
          </div>
          <button onClick={async () => { 
            if (!isRecording) { 
              try { 
                recorderRef.current = new AudioRecorder(); 
                await recorderRef.current.start(); 
                const stream = recorderRef.current.getStream(); 
                if (stream) setTimeout(() => startVisualizer(stream), 100); 
                setIsRecording(true); 
                setRecordingTime(0); 
                silenceStartRef.current = null; 
                isStoppingRef.current = false; 
                timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000); 
              } catch (e) { 
                console.error("Error starting recording:", e); 
              } 
            } else stopRecording(); 
          }} 
          disabled={isProcessing} 
          className={`relative flex items-center justify-center rounded-full transition-all ${isStandalone ? 'w-24 h-24' : 'w-14 h-14'} ${isRecording ? 'bg-red-500' : isProcessing ? 'bg-gray-700' : (engineMode === 'phd' ? 'bg-blue-600' : 'bg-indigo-600')}`}
        >
             {isProcessing ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></div> : isRecording ? <Square size={isStandalone ? 32 : 18} className="text-white fill-current" /> : (engineMode === 'phd' ? <Mic size={isStandalone ? 40 : 24} className="text-white" /> : <Activity size={isStandalone ? 40 : 24} className="text-white" />)}
          </button>
          {isStandalone && !isRecording && !isProcessing && (
             <div className="mt-4 flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest opacity-60">
                {engineMode === 'phd' ? <GraduationCap size={14}/> : <Activity size={14}/>}
                <span>Modo {engineMode === 'phd' ? 'PHD' : 'GM1'}</span>
             </div>
          )}
       </div>
    </div>
  );
};

export default DictaphoneMini;
